import { HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Alert } from "@models/alerts/alert";
import { AlertRecord } from "@models/alerts/alert-record";
import { AlertTypeIndicator } from "@models/alerts/alert-type-indicator";
import { AlertType } from "@models/alerts/alerts-enums";
import { PageResult } from "@models/requests/page-result.interface";
import { FileData, FileMetadata } from "@models/ui-models/files";
import { getInboundFilterModel } from "@pages/alerts/utils/get-inbound-filter-model";
import BaseApiService from "@shared/services/base/base-api.service";
import { chunkArray } from "@shared/utils/chunk-array";
import { IServerSideGetRowsRequest } from "ag-grid-enterprise";
import { generate, Observer } from "fast-json-patch";
import { concatMap, delay, forkJoin, from, map, Observable, of, toArray } from "rxjs";

@Injectable({ providedIn: 'root' })
export class AlertsApiService extends BaseApiService {
  resolve(alertId: string, actionId: string) {
    return this.api.post<Alert>(`${ this.apiUrl }/alerts/${ alertId }/resolve?actionId=${ actionId }`, {})
      .pipe(map(alert => { return new Alert(alert);}));
  }

  getAlert(alertId: string) {
    return this.api.get<Alert>(`${ this.apiUrl }/alerts/${ alertId }`);
  }

  getInboxAlertList(request: IServerSideGetRowsRequest, search?: string): Observable<PageResult<AlertRecord>> {
    let params = new HttpParams();
    if (search) {
      params = params.append('search', search);
    }

    return this.api.post<PageResult<AlertRecord>>(`${ this.apiUrl }/alerts/grid`, request, { params: params });
  }

  getAttachments(alertId: string): Observable<FileMetadata[]> {
    return this.api.get<FileMetadata[]>(`${ this.apiUrl }/alerts/${ alertId }/attachments`);
  }

  getDownload(alertId: string, filename?: string): Observable<string> {
    let params = new HttpParams();
    if (filename) {
      params = params.append('filename', filename);
    }

    return this.api.get<string>(`${ this.apiUrl }/alerts/${ alertId }/download`, { params: params });
  }

  downloadAllAttachments(alertId: string): Observable<FileData> {
    return this.api.get<FileData>(`${ this.apiUrl }/alerts/${ alertId }/download/all`);
  }

  updateAlert(alertId: string, observer: Observer<Alert>): Observable<Alert> {
    const patchOperations = observer ? generate(observer) : [];
    return this.api.patch<Alert>(`${ this.apiUrl }/alerts/${ alertId }`, patchOperations, { headers: { 'Content-Type': 'application/json-patch+json' } });
  }

  bulkUpdateAlert(alertIds: string[], observer: Observer<Alert[]>): Observable<Alert[]> {
    const patchOperations = observer ? generate(observer) : [];
    const batches = chunkArray(alertIds, 10);
    return from(batches).pipe(
      concatMap(batch => forkJoin(batch.map(alertId => {
        return this.api.patch<Alert>(`${ this.apiUrl }/alerts/${ alertId }`, patchOperations, { headers: { 'Content-Type': 'application/json-patch+json' } });
      })).pipe(
        delay(1000)
      )),
      toArray(),
      concatMap(responses => of(...responses).pipe(concatMap(res => res), toArray()))
    );
  }

  getBellInboxAlert(request?: IServerSideGetRowsRequest): Observable<AlertTypeIndicator[]> {
    if (!request) {
      request = {
        startRow: 0,
        endRow: 6,
        filterModel: getInboundFilterModel(),
        groupKeys: [],
        pivotCols: [],
        pivotMode: false,
        rowGroupCols: [],
        sortModel: [
          {
            sort: 'desc',
            colId: 'createdOn'
          }
        ],
        valueCols: []
      };
    }

    return this.api.post<PageResult<AlertTypeIndicator>>(`${ this.apiUrl }/alerts/grid`, request)
      .pipe(
        map(data => {
          return data.records.map(record => {
            if (record.type === AlertType.ValidationReport
              || record.type === AlertType.DebtReports
              || record.type === AlertType.AsicInvoice
              || record.type === AlertType.ChangeOfNameCertificate
              || record.type === AlertType.CompanyStatement
            ) {
              record.indicator = 'green';
            } else {
              record.indicator = 'red';
            }

            return record;
          });
        })
      );
  }
}
